<template>
  <RevTable
    v-if="methodsToDisplay.length"
    class=""
    :collection="methodsToDisplay"
    :columns
    transparent-header
  >
    <template #body-markets="{ item }">
      <div class="flex flex-wrap gap-6">
        <div v-for="{ market, subMarkets } in sortMarkets(item)" :key="market">
          <FlagDisplayer v-if="!subMarkets.length" :market />
          <div v-else class="flex flex-wrap gap-6">
            <FlagDisplayer
              v-for="subMarket in subMarkets"
              :key="subMarket.subMarketCode"
              :is-country="false"
              :market="
                market === MarketCountryCode.JP
                  ? subMarket.subMarketName
                  : subMarket.subMarketCode
              "
            />
          </div>
        </div>
      </div>
    </template>
    <template #body-shippingProvider="{ item }">
      {{ getShippingProviderLabel(categoryGroupMetadata, item) }}
    </template>
    <template #body-shippingTime="{ item }">
      {{ getShippingTimeLabel(item, getFormatHoursInDay) }}
    </template>
    <template #body-price="{ item }">
      {{ getPriceLabel(i18n, isFree, item) }}
    </template>
    <template #[`body-${TABLE_COLUMN_KEYS.DELETE}`]="{ item }">
      <RevButtonIcon
        v-if="isRemovable(item)"
        :icon="IconTrash"
        variant="secondary"
        @click="handleDeleteMethod(item)"
      />
      <RevTooltip v-if="!isRemovable(item)" position="left">
        <template #trigger="{ hide, show }">
          <RevButtonIcon
            disabled
            :icon="IconTrash"
            variant="secondary"
            @focusin="show"
            @focusout="hide"
            @mouseleave="hide"
            @mouseover="show"
          />
        </template>
        {{ i18n(translations.cannotDeleteFreeMethod) }}
      </RevTooltip>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { setFormValues } from '@ds/components/Form/Form.actions'
import { RevTable } from '@ds/components/Table'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconTrash } from '@ds/icons/IconTrash'

import { useFormatHours } from '~/composables/useFormatHours'

import { FORM_IDS } from '../../../../../../../../Shipping.constants'
import { intersect } from '../../../../../../../../Shipping.utils'
import categoryListTranslations from '../../../../../../CategoryList.translations'
import type { ShippingCategoryGroupMetadata } from '../../../../../../CategoryList.types'
import {
  getCarrier,
  getPriceLabel,
  getShippingProviderLabel,
  getShippingTimeLabel,
  sortMarkets,
} from '../../../../../../CategoryList.utils'
import FlagDisplayer from '../../../../../FlagDisplayer/FlagDisplayer.vue'
import type { FormMethodValues } from '../../../../RuleDrawer.types'
import { getCurrentMethodsKey } from '../../../../RuleDrawer.utils'

import translations from './MethodList.translations'

const TABLE_COLUMN_KEYS = {
  DELETE: 'delete',
}

const props = defineProps<{
  currentMethods: FormMethodValues[]
  otherMethods: FormMethodValues[]
  isFree: boolean
  isFreeMandatory: boolean
  categoryGroupMetadata: ShippingCategoryGroupMetadata
  shouldShowCollectionPointOptions?: boolean
}>()

const i18n = useI18n()
const emit = defineEmits<{
  (e: 'delete-method', methodKey: string): void
}>()

const { getFormatHoursInDay } = useFormatHours()

const hasSubMarkets = computed(() =>
  props.currentMethods.some(({ markets }) =>
    markets.some(({ subMarkets }) => subMarkets.length),
  ),
)

const columns = [
  {
    key: 'markets',
    label: i18n(
      hasSubMarkets.value ? translations.states : translations.markets,
    ),
  },
  {
    key: 'shippingProvider',
    label: i18n(categoryListTranslations.shippingProvider),
  },
  { key: 'shippingTime', label: i18n(categoryListTranslations.shippingTime) },
  { key: 'price', label: i18n(categoryListTranslations.price) },
  { key: TABLE_COLUMN_KEYS.DELETE, label: '', align: 'end' as const },
]

const methodsToDisplay = computed(() => {
  return props.currentMethods.filter(({ shippingProvider }) => {
    const carrier = getCarrier(
      props.categoryGroupMetadata.shippingMethods.providerOptions,
      shippingProvider,
    )
    const isCollectionPoint = carrier?.collectionPoint

    return props.shouldShowCollectionPointOptions
      ? isCollectionPoint
      : !isCollectionPoint
  })
})

const isRemovable = computed(() => (method: FormMethodValues) => {
  if (
    !props.isFree ||
    !props.isFreeMandatory ||
    props.shouldShowCollectionPointOptions
  ) {
    return true
  }

  const otherMethodsSubMarkets = props.otherMethods.flatMap(({ markets }) =>
    markets.flatMap(({ market, subMarkets }) => {
      const toReturn = []
      // in case there is no subMarkets, we only add the market
      if (!subMarkets.length) {
        toReturn.push(market)
      }
      // in case there is subMarkets, we add all of them
      toReturn.push(...subMarkets.map(({ subMarketCode }) => subMarketCode))

      return toReturn
    }),
  )
  const methodSubMarkets = method.markets.flatMap(({ market, subMarkets }) => {
    const toReturn = []
    // in case there is no subMarkets, we only add the markets
    if (!subMarkets.length) {
      toReturn.push(market)
    }
    // in case there is subMarkets, we add all of them
    toReturn.push(...subMarkets.map(({ subMarketCode }) => subMarketCode))

    return toReturn
  })

  // If free method, we need to be sure any of its markets is used in a paid method
  return !intersect(methodSubMarkets, otherMethodsSubMarkets).length
})

function handleDeleteMethod({ key: methodKey }: FormMethodValues) {
  setFormValues(FORM_IDS.SHIPPING_RULES, {
    [getCurrentMethodsKey(props.isFree)]: props.currentMethods.filter(
      ({ key }) => key !== methodKey,
    ),
  })

  emit('delete-method', methodKey)
}
</script>
